import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import styled from 'styled-components';
import { Menu, X } from 'lucide-react';

const Nav = styled.nav`
  position: sticky;
  top: 0;
  left: 0;
  right: 0;
  z-index: 1000;
  background: #ffffff;
  box-shadow: 0 2px 12px rgba(27, 58, 75, 0.08);
`;

const NavInner = styled.div`
  max-width: 1280px;
  margin: 0 auto;
  padding: 0 2rem;
  height: 70px;
  display: flex;
  align-items: center;
  justify-content: space-between;
`;

const LogoLink = styled(Link)`
  display: flex;
  align-items: center;
  flex-shrink: 0;
`;

const NavLinks = styled.ul`
  display: flex;
  align-items: center;
  gap: 2.5rem;
  list-style: none;

  @media (max-width: 900px) {
    position: fixed;
    top: 0;
    right: ${props => props.$open ? '0' : '-100%'};
    width: min(320px, 100vw);
    height: 100vh;
    background: ${props => props.theme.colors.navy};
    flex-direction: column;
    justify-content: center;
    gap: 2rem;
    transition: right 0.3s ease;
    z-index: 999;
    padding: 2rem;
  }
`;

const NavItem = styled(Link)`
  font-family: ${props => props.theme.fonts.sans};
  font-size: 0.9375rem;
  font-weight: 500;
  color: ${props => props.$active ? props.theme.colors.orange : props.theme.colors.navy};
  position: relative;
  padding-bottom: 2px;
  transition: color 0.2s;

  &::after {
    content: '';
    position: absolute;
    bottom: -2px;
    left: 0;
    width: ${props => props.$active ? '100%' : '0'};
    height: 2px;
    background: ${props => props.theme.colors.orange};
    transition: width 0.25s ease;
  }

  &:hover {
    color: ${props => props.theme.colors.orange};
    &::after { width: 100%; }
  }

  @media (max-width: 900px) {
    color: ${props => props.$active ? props.theme.colors.orange : '#ffffff'};
    font-size: 1.125rem;
  }
`;

const NavCtaGroup = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;

  @media (max-width: 900px) {
    flex-direction: column;
    gap: 0.75rem;
    width: 100%;
  }
`;

const BtnPrimary = styled(Link)`
  display: inline-flex;
  align-items: center;
  background: ${props => props.theme.colors.orange};
  color: #ffffff;
  font-family: ${props => props.theme.fonts.sans};
  font-size: 0.875rem;
  font-weight: 600;
  padding: 0.575rem 1.25rem;
  border-radius: ${props => props.theme.borderRadius.full};
  transition: background 0.2s, transform 0.15s;
  white-space: nowrap;

  &:hover { background: #e07520; transform: translateY(-1px); }

  @media (max-width: 900px) {
    font-size: 1rem;
    padding: 0.75rem 2rem;
    width: 100%;
    justify-content: center;
  }
`;

const BtnSecondary = styled(Link)`
  display: inline-flex;
  align-items: center;
  background: transparent;
  color: ${props => props.theme.colors.navy};
  border: 1.5px solid ${props => props.theme.colors.navy};
  font-family: ${props => props.theme.fonts.sans};
  font-size: 0.875rem;
  font-weight: 600;
  padding: 0.525rem 1.175rem;
  border-radius: ${props => props.theme.borderRadius.full};
  transition: background 0.2s, color 0.2s;
  white-space: nowrap;

  &:hover {
    background: ${props => props.theme.colors.navy};
    color: #ffffff;
  }

  @media (max-width: 900px) {
    font-size: 1rem;
    padding: 0.7rem 2rem;
    width: 100%;
    justify-content: center;
    color: #ffffff;
    border-color: rgba(255,255,255,0.5);
    &:hover { background: rgba(255,255,255,0.1); color: #ffffff; }
  }
`;

const Overlay = styled.div`
  display: none;
  @media (max-width: 900px) {
    display: ${props => props.$open ? 'block' : 'none'};
    position: fixed;
    inset: 0;
    background: rgba(0,0,0,0.45);
    z-index: 998;
  }
`;

const HamburgerBtn = styled.button`
  display: none;
  color: ${props => props.theme.colors.navy};
  z-index: 1001;
  padding: 4px;

  @media (max-width: 900px) {
    display: block;
  }
`;

const CloseBtn = styled.button`
  display: none;
  @media (max-width: 900px) {
    display: block;
    position: absolute;
    top: 1.25rem;
    right: 1.25rem;
    color: rgba(255,255,255,0.8);
  }
`;

function Navigation() {
  const [open, setOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    setOpen(false);
  }, [location]);

  useEffect(() => {
    document.body.style.overflow = open ? 'hidden' : '';
    return () => { document.body.style.overflow = ''; };
  }, [open]);

  const links = [
    { to: '/', label: 'Home' },
    { to: '/listings', label: 'Listings' },
    { to: '/about', label: 'About' },
    { to: '/admin', label: 'Admin', mobileOnly: true },
  ];

  return (
    <>
      <Nav>
        <NavInner>
          <LogoLink to="/">
            <img src="/inbookingslogo.jpg" alt="InBookings" style={{ height: '44px', width: 'auto', display: 'block' }} />
          </LogoLink>

          <NavLinks $open={open}>
            <CloseBtn onClick={() => setOpen(false)} aria-label="Close menu">
              <X size={26} />
            </CloseBtn>
            {links.map(({ to, label, mobileOnly }) => (
              <li key={to} className={mobileOnly ? 'nav-mobile-only' : undefined}>
                <NavItem to={to} $active={location.pathname === to}>
                  {label}
                </NavItem>
              </li>
            ))}
            <li>
              <NavCtaGroup>
                <BtnSecondary to="/list-your-space">List Your Space</BtnSecondary>
                <BtnPrimary to="/listings">Book a Space</BtnPrimary>
              </NavCtaGroup>
            </li>
          </NavLinks>

          <HamburgerBtn onClick={() => setOpen(true)} aria-label="Open menu">
            <Menu size={26} />
          </HamburgerBtn>
        </NavInner>
      </Nav>

      <Overlay $open={open} onClick={() => setOpen(false)} />
      <style>{`.nav-mobile-only { display: none; } @media (max-width: 900px) { .nav-mobile-only { display: block; } }`}</style>
    </>
  );
}

export default Navigation;
